import { format, formatDistanceToNow } from 'date-fns';
import { upperFirst } from 'lodash';
import { InfoTitle } from '../components/common/InfoTooltip/InfoTitle';
import { MULTIPLE_LABEL } from '../constants/approvedStatus';
import { OrderTypesDisplayName } from '../constants/orderTypes';
import { DATE_FORMAT_US, PRICING_DATETIME_FORMAT } from '../constants/dateFormats';
import { NO_VALUE_PLACEHOLDER } from '../constants/tables';
import { CURRENCY } from '../constants/analytics';
import { RETAILER_PRODUCTS_CURRENCY_LEVELS } from '../constants/pricings';

export const currencyFormat = (value, currency) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency ?? CURRENCY.USD,
    minimumFractionDigits: 0,
  }).format(value / 100);
};

export const retailerCurrencyFormat = (value, retailer) => {
  const currency = getRetailerCurrency(retailer);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency ?? CURRENCY.USD,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const getRetailerCurrency = (retailer = {}) => {
  // First try to get from pricing_currencies (used in some contexts)
  if (retailer.pricing_currencies?.length > 0) {
    return retailer.pricing_currencies[0].toUpperCase();
  }

  // Then try to get from ecommerce_products_integration.pricing.currencies
  const { currencies = [] } = retailer?.ecommerce_products_integration?.pricing || {};
  if (currencies?.length > 0) {
    const mandatoryCurrency = currencies.find(
      (currency) => currency.level === RETAILER_PRODUCTS_CURRENCY_LEVELS.MANDATORY,
    );

    if (mandatoryCurrency) {
      return mandatoryCurrency.currency.toUpperCase();
    }

    // Fallback to first currency if no mandatory currency found
    return currencies[0].currency.toUpperCase();
  }

  // Default fallback
  return CURRENCY.USD.toUpperCase();
};

export const ratePercentageFormat = (value) => {
  return `${(value * 100).toFixed(2)}%`;
};

export const convertedCurrencyFormat = (value, currency, conversionRate, orderExchangeRate) => {
  const orderConversionRate = orderExchangeRate ? orderExchangeRate.conversion_rate : conversionRate;
  const orderCurrency = orderExchangeRate ? orderExchangeRate.target_currency : currency;
  if (orderConversionRate) {
    value = Math.floor(value * orderConversionRate);
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: orderCurrency,
  }).format(value / 100);
};

export const numberFormatter = (value) => value.toString().replace(/\D/g, '');
export const decimalFormatter = (value) => value.toString().replace(/([^\d.])+/g, '');
export const dateFormatter = (date) => format(new Date(date), 'P');
export const dateFormatterForUS = (date) => format(new Date(date), DATE_FORMAT_US);
export const datetimeFormatter = (date) => format(new Date(date), 'Pp');
export const timeAgoFormatter = (value) => formatDistanceToNow(new Date(value), { addSuffix: true });
export const numberOrZeroFormatter = (cell) => (cell ? <span>{cell}</span> : 0);
export const stringOrDashFormatter = (cell) => (cell ? <span>{cell}</span> : NO_VALUE_PLACEHOLDER);
export const stringOrNAFormatter = (cell) => (cell ? <span>{cell}</span> : 'N/A');

export const formatMany = (count, text, withCount = false) => {
  const noun = count === 1 ? text : `${text}s`;
  return withCount ? `${count} ${noun}` : noun;
};

export const orderTypeFormatter = (orderType) => {
  return OrderTypesDisplayName[orderType] || upperFirst(orderType);
};

export function formatPricingDate(date) {
  if (!date) return NO_VALUE_PLACEHOLDER;
  if (date === MULTIPLE_LABEL) return MULTIPLE_LABEL;

  const dates = date.split(' - ');
  return dates.map((date) => format(new Date(date), PRICING_DATETIME_FORMAT)).join(' - ');
}

export const longContentFormatter = (content, characterCount = 30) => {
  if (!content || content.length < characterCount) {
    return <span>{content}</span>;
  }

  return (
    <InfoTitle content={content}>
      <span>{content.slice(0, characterCount)}&hellip;</span>
    </InfoTitle>
  );
};
